/**
 * RegisterOcrScan — "Scan (OCR)" on a register form (web escalation path).
 *
 * Web has no on-device OCR, so this uses the Claude-vision escalation: pick/photograph
 * the paper register → store the scan → enqueue a job → poll for the brain's draft →
 * pre-fill the register form (the operator then reviews + Submits). The platform makes
 * zero AI calls; if the brain PC isn't running the job stays queued and we tell the
 * operator to type instead. Nothing is written unattended.
 */

import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Loader2, ScanLine } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { useToast } from "@/lib/ui/toast";
import { backendErrorMessage, ocr, type OCRJob, type RegisterField } from "@/lib/api";
import { prepareImageForUpload } from "@/lib/images/compress";

type ScanState = "idle" | "uploading" | "processing" | "failed";

export function RegisterOcrScan({
  registerId,
  fields,
  onApply,
}: {
  registerId: string;
  fields: RegisterField[];
  onApply: (values: Record<string, string>, sourceImageId: string) => void;
}) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<number | null>(null);
  const imageRef = useRef<string | null>(null);
  const [state, setState] = useState<ScanState>("idle");

  // Stop polling on unmount.
  useEffect(
    () => () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    },
    [],
  );

  const applyDraft = (job: OCRJob) => {
    const draft = job.draft ?? {};
    const values: Record<string, string> = {};
    for (const f of fields) {
      const v = draft[f.metric];
      if (v !== undefined && v !== null) values[f.metric] = String(v);
    }
    onApply(values, imageRef.current ?? job.image_id);
  };

  const run = async (file: File) => {
    setState("uploading");
    try {
      // A register scan needs legible text for OCR — preserve quality, cap dimension.
      const prepared = await prepareImageForUpload(file, {
        compress: false,
        maxDimension: 3000,
      });
      const img = await ocr.scan(registerId, {
        file: prepared.blob,
        compressed: prepared.compressed,
        width: prepared.width,
        height: prepared.height,
        filename: file.name,
      });
      imageRef.current = img.image_id;
      const job = await ocr.enqueue(registerId, img.image_id);
      setState("processing");

      let tries = 0;
      pollRef.current = window.setInterval(() => {
        tries += 1;
        ocr
          .job(job.id)
          .then((j) => {
            if (j.status === "done") {
              if (pollRef.current !== null) window.clearInterval(pollRef.current);
              setState("idle");
              applyDraft(j);
              toast.push("ok", "OCR draft ready — review the values, then Submit.");
            } else if (j.status === "failed") {
              if (pollRef.current !== null) window.clearInterval(pollRef.current);
              setState("failed");
              toast.push("error", "OCR couldn't read the scan. Type the values instead.");
            } else if (tries > 40) {
              // ~2 min — the reader PC may be offline; don't spin forever.
              if (pollRef.current !== null) window.clearInterval(pollRef.current);
              setState("failed");
              toast.push(
                "info",
                "OCR is still queued (the reader may be offline). Type the values, or try later.",
              );
            }
          })
          .catch(() => {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
            setState("failed");
          });
      }, 3000);
    } catch (err) {
      setState("failed");
      toast.push("error", backendErrorMessage(err) || "Couldn't upload the scan.");
    }
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void run(file);
    e.target.value = "";
  };

  const busy = state === "uploading" || state === "processing";

  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
      />
      <Button
        size="sm"
        variant="secondary"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
      >
        {busy ? (
          <Loader2 className="mr-1.5 size-4 animate-spin" />
        ) : (
          <ScanLine className="mr-1.5 size-4" />
        )}
        {state === "uploading"
          ? "Uploading…"
          : state === "processing"
            ? "Reading (Claude)…"
            : "Scan (OCR)"}
      </Button>
      {state === "failed" && (
        <span className="text-xs text-on-surface-muted">Couldn&apos;t read it — type below.</span>
      )}
    </div>
  );
}
